import React, { useEffect, useCallback, useRef } from 'react'

interface DetectBlurProps {
  children: React.ReactNode
  event?: string
  active: boolean
  detectEscape?: boolean
  disableTouchEnd?: boolean
  onClickOutside: (e: Event) => void
}

export const DetectBlur: React.JSX.CFC<DetectBlurProps> = ({
  children,
  event = 'mouseup',
  active = false,
  detectEscape = false,
  disableTouchEnd = false,
  onClickOutside,
}) => {
  const node = useRef<HTMLDivElement | null>(null)

  const handleClick = useCallback(
    (evt: Event) => {
      if (!active || node === null || node.current === null) {
        return
      }

      const target = evt.target as Node
      const targetElement =
        target instanceof Element ? target : target.parentElement

      if (
        node.current.contains(target) ||
        targetElement?.closest('[data-detect-blur-ignore="true"]')
      ) {
        return
      }

      onClickOutside(evt)
    },
    [active, onClickOutside]
  )

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (!active) return

      if (evt.key?.toLowerCase() === 'escape') {
        onClickOutside(evt)
      }
    },
    [active, onClickOutside]
  )

  useEffect(() => {
    if (event) document.addEventListener(event, handleClick)
    if (!disableTouchEnd) document.addEventListener('touchend', handleClick)
    if (detectEscape) document.addEventListener('keydown', handleKeyDown)

    return () => {
      if (event) document.removeEventListener(event, handleClick)
      if (!disableTouchEnd) document.addEventListener('touchend', handleClick)
      if (detectEscape) document.removeEventListener('keydown', handleKeyDown)
    }
  }, [event, detectEscape, disableTouchEnd, handleClick, handleKeyDown])

  return <div ref={node}>{children}</div>
}
